/**
 * HTTP client.
 *
 * Pipeline per request:
 *
 *   1. build config (url, method, body, headers, signal)
 *   2. run request interceptors          ← attach auth, tenant, trace
 *   3. fetch with timeout (AbortController)
 *   4. parse JSON (or fall back to text)
 *   5. throw typed error on non-2xx      ← errors.fromResponse
 *   6. run response interceptors         ← unwrap envelopes, log
 *   7. on error: run error interceptors  ← refresh-then-retry on 401
 *
 * Services call `http.get('/trademarks', { params, signal })`.
 * They never see the raw Response object.
 *
 * Default exports a singleton `http` so most callers don't have
 * to manage a client instance.
 */

import config from './config'
import { interceptors, runRequest, runResponse, runError } from './interceptors'
import { NetworkError, TimeoutError, fromResponse } from './errors'
import type { RequestConfig, RequestInitOptions } from './types'

const JSON_HEADERS = { 'Content-Type': 'application/json' }

function buildUrl(path: string, params?: Record<string, unknown> | null): string {
  const base = config.api.baseUrl.replace(/\/+$/, '')
  /* Idempotent on purpose. Error interceptors replay a request by handing
   * back `cfg.url`, which is already absolute — prefixing it a second time
   * produced /api/api/auth/me and turned every retry into a 404. */
  const alreadyBuilt = /^https?:\/\//.test(path) || (base !== '' && path.startsWith(`${base}/`))
  const url = alreadyBuilt ? path : `${base}/${path.replace(/^\/+/, '')}`
  if (!params) return url
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue
    if (Array.isArray(v)) v.forEach((x) => qs.append(k, x))
    else qs.append(k, String(v))
  }
  const s = qs.toString()
  return s ? `${url}?${s}` : url
}

async function parseBody(
  response: Response,
  responseType: 'json' | 'blob' = 'json'
): Promise<unknown> {
  const ct = response.headers.get('Content-Type') || ''
  if (response.status === 204 || ct === '') return null
  /* Asked for bytes: hand back bytes. Falling through to `.text()` below would
   * decode a PDF as UTF-8 and produce a file nothing can open. */
  if (responseType === 'blob') return await response.blob()
  if (ct.includes('application/json')) {
    try {
      return await response.json()
    } catch {
      return null
    }
  }
  return response.text()
}

async function request(method: string, path: string, init: RequestInitOptions = {}): Promise<any> {
  /* 1. Build config — pass via interceptors so they can rewrite it. */
  let cfg: RequestConfig = {
    method,
    url: buildUrl(path, init.params),
    headers: { ...config.api.headers, ...(init.headers || {}) },
    body: init.body !== undefined ? init.body : null,
    json: init.json /* shortcut: object → JSON.stringify */,
    signal: init.signal ?? null,
    timeoutMs: init.timeoutMs ?? config.api.timeoutMs,
    credentials: init.credentials ?? 'include',
    responseType: init.responseType ?? 'json',
    meta: init.meta ?? {} /* free-form bag for interceptors */,
  }
  cfg = await runRequest(cfg)

  if (cfg.json !== undefined) {
    cfg.headers = { ...cfg.headers, ...JSON_HEADERS }
    cfg.body = JSON.stringify(cfg.json)
  }

  /* 3. Fetch with timeout. */
  const ac = new AbortController()
  const onAbort = () => ac.abort()
  if (cfg.signal) cfg.signal.addEventListener('abort', onAbort)
  const timer = cfg.timeoutMs ? setTimeout(() => ac.abort('timeout'), cfg.timeoutMs) : null

  try {
    const response = await fetch(cfg.url, {
      method: cfg.method,
      headers: cfg.headers,
      body: cfg.body ?? undefined,
      credentials: cfg.credentials,
      signal: ac.signal,
    })

    /* An error response is JSON even when the request asked for bytes — the
     * error envelope must stay readable, or a failed download surfaces as an
     * unreadable Blob instead of a message. */
    const body = await parseBody(response, response.ok ? cfg.responseType : 'json')
    if (!response.ok) throw fromResponse(response, body as Record<string, any> | null)

    /* 6. Response interceptors get the raw parsed body. */
    return await runResponse(body, cfg)
  } catch (err) {
    const failure = err as { name?: string; message?: string } | undefined
    let normalized: unknown = err
    if (failure?.name === 'AbortError' || ac.signal.reason === 'timeout') {
      normalized =
        ac.signal.reason === 'timeout'
          ? new TimeoutError(`Request to ${cfg.url} timed out after ${cfg.timeoutMs}ms`)
          : err
    } else if (failure?.message?.includes('Failed to fetch')) {
      normalized = new NetworkError(`Cannot reach ${cfg.url}`, err)
    }
    return await runError(normalized, cfg)
  } finally {
    if (timer) clearTimeout(timer)
    if (cfg.signal) cfg.signal.removeEventListener('abort', onAbort)
  }
}

/** The singleton client most services use. */
export const http = {
  get: (path: string, init?: RequestInitOptions) => request('GET', path, init),
  delete: (path: string, init?: RequestInitOptions) => request('DELETE', path, init),
  post: (path: string, json?: unknown, init?: RequestInitOptions) =>
    request('POST', path, { ...init, json }),
  put: (path: string, json?: unknown, init?: RequestInitOptions) =>
    request('PUT', path, { ...init, json }),
  patch: (path: string, json?: unknown, init?: RequestInitOptions) =>
    request('PATCH', path, { ...init, json }),
  /** Lower-level escape hatch. */
  request,
}

/* ──────────────────────────────────────────────────────────────
 * Default interceptors installed at module load.
 * ────────────────────────────────────────────────────────────── */

import { getAccessToken } from './tokens'

/** Attach Bearer token if one is present. */
interceptors.request.use((cfg) => {
  const token = getAccessToken()
  if (token && !cfg.headers.Authorization) {
    cfg.headers.Authorization = `Bearer ${token}`
  }
  return cfg
})

/**
 * Auto-retry for transient errors.
 *
 * Per-request opt-out via `meta.noRetry = true`. Defaults to 2
 * additional attempts with exponential backoff (200ms / 400ms).
 * Only transient errors (network / timeout / 502 / 503 / 504)
 * retry — auth + validation + 404 pass through immediately.
 */
import { withRetry, isTransient } from './errors'

interceptors.error.use(async (err, reqConfig) => {
  if (reqConfig?.meta?.noRetry) throw err
  if (!isTransient(err)) throw err

  /* Mark the config so the inner request() knows this is a retry
   * (prevents infinite loops if the retry interceptor is hit
   * again — withRetry's own counter handles further retries). */
  if (reqConfig?.meta?._retrying) throw err
  if (reqConfig) reqConfig.meta = { ...(reqConfig.meta || {}), _retrying: true }

  /* Re-issue the same request via withRetry, which retries the
   * given thunk with exponential backoff on transient errors. */
  return await withRetry(() => request(reqConfig.method, reqConfig.url, reqConfig), {
    attempts: 3,
    baseDelayMs: 200,
    factor: 2,
  })
})

/* ──────────────────────────────────────────────────────────────
 * Silent refresh (W3.M4).
 * ────────────────────────────────────────────────────────────── */

import { shouldRefresh, getRefreshToken } from './tokens'

/**
 * The in-flight refresh, shared by every caller.
 *
 * Single-flight is the whole point. A page typically fires several
 * requests at once, so an expired token produces a burst of 401s; without
 * this, each would start its own refresh, and since the backend *rotates*
 * the refresh token on use, the first exchange invalidates the token the
 * others are still holding — they fail, and a session that only needed
 * one refresh gets logged out instead.
 */
let refreshing: Promise<unknown> | null = null

/** Broadcast when refreshing is impossible, so the app can exit to /login. */
export type SessionEndedHandler = () => void
let onSessionEnded: SessionEndedHandler | null = null
export function setSessionEndedHandler(fn: SessionEndedHandler | null): void {
  onSessionEnded = fn
}

function refreshOnce(): Promise<unknown> {
  if (!refreshing) {
    refreshing = import('@/services/auth.service')
      .then((m) => m.default.refresh())
      .finally(() => {
        refreshing = null
      })
  }
  return refreshing
}

/** Auth calls opt out: a 401 from /auth/login means "wrong password". */
function optedOut(cfg?: RequestConfig): boolean {
  return Boolean(cfg?.meta?.noRefresh)
}

/**
 * Proactive leg: if the token is within the leeway of expiring, renew it
 * *before* sending, so the common case never costs a failed round trip.
 */
interceptors.request.use(async (cfg) => {
  if (optedOut(cfg) || !getRefreshToken() || !shouldRefresh()) return cfg
  try {
    await refreshOnce()
    const fresh = getAccessToken()
    if (fresh) cfg.headers = { ...cfg.headers, Authorization: `Bearer ${fresh}` }
  } catch {
    /* Fall through and let the request 401 — the reactive leg below owns
     * the decision to end the session, so it is made in exactly one place. */
  }
  return cfg
})

/**
 * Reactive leg: a 401 we did not expect. Refresh once, replay the original
 * request with the new token, and give up (ending the session) if that
 * fails. 403 is deliberately excluded — it means "authenticated but not
 * allowed", which no amount of refreshing fixes.
 */
interceptors.error.use(async (err, reqConfig) => {
  const failure = err as { name?: string; status?: number } | undefined
  if (failure?.name !== 'AuthError' || failure.status !== 401) throw err
  if (optedOut(reqConfig) || reqConfig?.meta?._replayed) throw err
  if (!getRefreshToken()) {
    onSessionEnded?.()
    throw err
  }

  try {
    await refreshOnce()
  } catch {
    onSessionEnded?.()
    throw err
  }

  const token = getAccessToken()
  return await request(reqConfig.method, reqConfig.url, {
    ...reqConfig,
    headers: { ...reqConfig.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    meta: { ...(reqConfig.meta || {}), _replayed: true },
  })
})

export default http
